import type { BridgeController, ProviderBridge } from "./types"

// YouTube IFrame Player API bridge.
//
// Why a bridge: the previous shape threaded `muted` through the iframe src,
// which forced a full reload on every toggle — black flash, lost playback
// position, occasional audio-resume-after-mute glitches. The IFrame API
// exposes `player.mute()` / `player.unMute()` / `player.pauseVideo()` over
// postMessage, so we can drive playback without touching the src after
// mount. Backend ships `?enablejsapi=1` on every YouTube embed URL so the
// API is reachable.
//
// Reference: https://developers.google.com/youtube/iframe_api_reference
//
// No test harness yet — verify manually by toggling mute on /playlists/:id/
// play with a YouTube track. Pre-bridge the iframe flashed; post-bridge it
// doesn't.

const YT_API_SRC = "https://www.youtube.com/iframe_api"

// PlayerState constants per the IFrame API. ENDED closes the track; PLAYING
// and PAUSED drive the open audio stage's spinning record. (BUFFERING/CUED
// are intentionally not "playing" — the disc holds still until sound runs.)
const YT_STATE_ENDED = 0
const YT_STATE_PLAYING = 1
const YT_STATE_PAUSED = 2

interface YTPlayer {
  playVideo(): void
  pauseVideo(): void
  mute(): void
  unMute(): void
  setVolume(volume: number): void
  destroy(): void
  addEventListener?(event: string, cb: string): void
}

type YTConstructor = new (
  target: HTMLIFrameElement | string,
  config: {
    events?: {
      onReady?: (e: { target: YTPlayer }) => void
      onStateChange?: (e: { data: number }) => void
    }
  },
) => YTPlayer

interface YTNamespace {
  Player: YTConstructor
}

declare global {
  interface Window {
    YT?: YTNamespace
    onYouTubeIframeAPIReady?: () => void
  }
}

// The API script registers a single global callback. Multiple attach() calls
// stack into this queue so all of them get invoked once the script loads.
let apiLoadPromise: Promise<void> | null = null

function loadYouTubeApi(): Promise<void> {
  if (apiLoadPromise) return apiLoadPromise
  if (typeof window === "undefined") {
    return Promise.reject(new Error("YouTube bridge requires window"))
  }
  if (window.YT?.Player) return Promise.resolve()

  apiLoadPromise = new Promise<void>((resolve) => {
    const prev = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      prev?.()
      resolve()
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${YT_API_SRC}"]`,
    )
    if (!existing) {
      const tag = document.createElement("script")
      tag.src = YT_API_SRC
      document.head.appendChild(tag)
    }
  })

  return apiLoadPromise
}

export const youtubeBridge: ProviderBridge = {
  matches: (url) =>
    url.startsWith("https://www.youtube.com/embed/") ||
    url.startsWith("https://youtube.com/embed/"),

  attach: (iframe) => {
    let player: YTPlayer | null = null
    // Constructed ≠ ready: `new YT.Player(...)` returns synchronously but
    // the API methods (setVolume included) only exist after onReady — a
    // command issued in that window must queue, not call.
    let ready = false
    // destroy() before the API script resolves must cancel the pending
    // construction. Otherwise StrictMode's dev double-effect (attach →
    // destroy → attach) constructs TWO players on one iframe — the zombie's
    // widget binding races the live one's and the postMessage channel goes
    // dead, so every later command (pause, the unmute ramp) silently
    // no-ops.
    let destroyed = false
    const endedListeners = new Set<() => void>()
    const playStateListeners = new Set<(playing: boolean) => void>()
    let lastPlaying = false
    // Buffer commands issued before the player is ready so the shell can
    // call setMuted() at mount without losing the call to a race.
    const pending: ((p: YTPlayer) => void)[] = []

    loadYouTubeApi()
      .then(() => {
        if (destroyed || !window.YT?.Player) return
        player = new window.YT.Player(iframe, {
          events: {
            onReady: () => {
              ready = true
              while (pending.length && player) pending.shift()!(player)
            },
            onStateChange: (e) => {
              if (e.data === YT_STATE_ENDED) {
                endedListeners.forEach((cb) => cb())
              }
              if (
                e.data === YT_STATE_PLAYING ||
                e.data === YT_STATE_PAUSED ||
                e.data === YT_STATE_ENDED
              ) {
                const playing = e.data === YT_STATE_PLAYING
                if (playing !== lastPlaying) {
                  lastPlaying = playing
                  playStateListeners.forEach((cb) => cb(playing))
                }
              }
            },
          },
        })
      })
      .catch(() => {
        // Network failure or blocked third-party script. Playback still works
        // via the iframe's own controls; we just lose API-driven mute/pause.
      })

    const enqueue = (fn: (p: YTPlayer) => void) => {
      if (player && ready) fn(player)
      else pending.push(fn)
    }

    const controller: BridgeController = {
      play: () => enqueue((p) => p.playVideo()),
      pause: () => enqueue((p) => p.pauseVideo()),
      setMuted: (muted) =>
        enqueue((p) => {
          if (muted) {
            p.mute()
            return
          }
          // Unmute with a ~200ms volume ramp (0 → 100 in 25-point steps)
          // so audio enters like a fader, not a cut. `enqueue` guarantees
          // the player is ready before the first step; the try/catch covers
          // a player destroyed mid-ramp (slide unmounted).
          p.setVolume(0)
          p.unMute()
          let volume = 0
          const step = () => {
            try {
              volume += 25
              p.setVolume(Math.min(volume, 100))
              if (volume < 100) window.setTimeout(step, 50)
            } catch {
              // Player destroyed mid-ramp; nothing left to ramp.
            }
          }
          window.setTimeout(step, 50)
        }),
      subscribeEnded: (cb) => {
        endedListeners.add(cb)
        return () => {
          endedListeners.delete(cb)
        }
      },
      subscribePlayState: (cb) => {
        playStateListeners.add(cb)
        cb(lastPlaying)
        return () => {
          playStateListeners.delete(cb)
        }
      },
      destroy: () => {
        destroyed = true
        endedListeners.clear()
        playStateListeners.clear()
        try {
          player?.destroy()
        } catch {
          // Destroy can throw if the iframe was already removed from the DOM.
        }
        player = null
      },
    }

    return controller
  },
}
